import { useEffect } from "react";

const running = new Map<HTMLElement, Animation>();

export function playControlFeedback(target: HTMLElement, confirmed = false): void {
  if (
    !target.isConnected ||
    document.hidden ||
    typeof target.animate !== "function" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
    return;
  const root = document.documentElement;
  const strength =
    root.dataset.effectLighting === "off"
      ? 0
      : Number(root.style.getPropertyValue("--effect-lighting"));
  if (!(strength > 0)) return;
  running.get(target)?.cancel();
  if (running.size >= 8) {
    const first = running.entries().next().value;
    if (first) {
      first[1].cancel();
      running.delete(first[0]);
    }
  }
  const color =
    getComputedStyle(target).getPropertyValue("--quality").trim() ||
    getComputedStyle(root)
      .getPropertyValue(confirmed ? "--mana" : "--copper-bright")
      .trim();
  const glow = `color-mix(in srgb, ${color} ${Math.round(Math.min(1, strength) * 55)}%, transparent)`;
  const animation = target.animate(
    [
      {
        boxShadow: `inset 0 0 0 1px ${glow}, inset 0 0 ${confirmed ? 22 : 12}px ${glow}`,
        opacity: 1,
      },
      { boxShadow: `inset 0 0 0 1px ${glow}, inset 0 0 0 transparent`, opacity: 1 },
      { boxShadow: "inset 0 0 0 0 transparent", opacity: 1 },
    ],
    { duration: confirmed ? 420 : 220, easing: "ease-out" },
  );
  animation.effect?.updateTiming({
    duration: (confirmed ? 420 : 220) * Math.min(1, Math.max(0.35, strength)),
  });
  running.set(target, animation);
  const clear = () => {
    if (running.get(target) === animation) running.delete(target);
  };
  animation.onfinish = clear;
  animation.oncancel = clear;
}

export function useUIFeedback(): void {
  useEffect(() => {
    const clear = () => {
      running.forEach((animation) => animation.cancel());
      running.clear();
    };
    const click = (event: MouseEvent) => {
      const target =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>("button, summary")
          : null;
      if (
        !target ||
        target.matches(":disabled, [aria-disabled=true]") ||
        target.closest(".board-host")
      )
        return;
      playControlFeedback(target);
    };
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const settingsObserver = new MutationObserver(clear);
    settingsObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-effect-lighting", "style"],
    });
    document.addEventListener("click", click);
    document.addEventListener("visibilitychange", clear);
    media.addEventListener("change", clear);
    return () => {
      clear();
      settingsObserver.disconnect();
      document.removeEventListener("click", click);
      document.removeEventListener("visibilitychange", clear);
      media.removeEventListener("change", clear);
    };
  }, []);
}
